import apiService from './apiService';

const PREFIX = '/klub';

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

const klubService = {
  async getEtapeMail(token) {
    const res = await request(apiService.get(`${PREFIX}/etape-mail/${encodeURIComponent(token)}`));
    return res.data;
  },

  async confirmEtapeMail(token) {
    const res = await request(apiService.post(`${PREFIX}/etape-mail/${encodeURIComponent(token)}`));
    return res.data;
  },

  async getMyFollowups() {
    const res = await request(apiService.get(`${PREFIX}/my-followups`));
    return {
      followups: res.data.followups ?? [],
      preavis: res.data.preavis ?? [],
    };
  },

  async getStats() {
    const res = await request(apiService.get(`${PREFIX}/stats`));
    return res.data.stats ?? {};
  },

  async listAdherents({
    search = '',
    formule = '',
    statut = '',
    commercial_id = '',
    dolibarr_match_status = '',
    current_workflow_step_code = '',
    lifecycle_phase = '',
    preavis = '',
    mois = '',
    dolibarr_detected = false,
    renouvellement_60j = false,
    page = 1,
    limit = 50,
  } = {}) {
    const res = await request(apiService.get(`${PREFIX}/adherents`, {
      search,
      formule,
      statut,
      commercial_id,
      dolibarr_match_status,
      current_workflow_step_code,
      lifecycle_phase,
      preavis,
      mois,
      dolibarr_detected: dolibarr_detected ? 1 : '',
      renouvellement_60j: renouvellement_60j ? 1 : '',
      page,
      limit,
    }));
    return { adherents: res.data.adherents ?? [], pagination: res.pagination ?? {} };
  },

  async getAdherent(id) {
    const res = await request(apiService.get(`${PREFIX}/adherents/${id}`));
    return res.data.adherent ?? null;
  },

  async createAdherent(data) {
    const res = await request(apiService.post(`${PREFIX}/adherents`, data));
    return res.data.adherent;
  },

  async updateAdherent(id, data) {
    const res = await request(apiService.put(`${PREFIX}/adherents/${id}`, data));
    return res.data.adherent;
  },

  async deleteAdherent(id, { permanent = false } = {}) {
    const params = permanent ? { permanent: '1' } : {};
    await request(apiService.delete(`${PREFIX}/adherents/${id}`, params));
  },

  async setCurrentWorkflowStep(adherentId, stepCode, extra = {}) {
    const res = await request(apiService.patch(`${PREFIX}/adherents/${adherentId}/workflow-current`, {
      step_code: stepCode,
      ...extra,
    }));
    return res.data.adherent;
  },

  async updateWorkflowStep(adherentId, stepCode, data) {
    const res = await request(apiService.patch(`${PREFIX}/adherents/${adherentId}/workflow/${stepCode}`, data));
    return res.data.workflow_step;
  },

  async updateWorkflow(adherentId, steps) {
    const res = await request(apiService.patch(`${PREFIX}/adherents/${adherentId}/workflow`, { steps }));
    return res.data.adherent;
  },

  async getDolibarrSuggestions(adherentId) {
    const res = await request(apiService.get(`${PREFIX}/adherents/${adherentId}/dolibarr-suggestions`));
    return res.data.suggestions ?? [];
  },

  async updateDolibarrLink(adherentId, data) {
    const res = await request(apiService.patch(`${PREFIX}/adherents/${adherentId}/dolibarr-link`, data));
    return res.data.adherent;
  },

  async listCe({ search = '', listing_donne = '', acces_cartes = '', etape = '', commercial_id = '', page = 1, limit = 50 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/ce`, {
      search, listing_donne, acces_cartes, etape, commercial_id, page, limit,
    }));
    return { ce: res.data.ce ?? [], pagination: res.pagination ?? {} };
  },

  async getCe(id) {
    const res = await request(apiService.get(`${PREFIX}/ce/${id}`));
    return res.data.ce ?? null;
  },

  async createCe(data) {
    const res = await request(apiService.post(`${PREFIX}/ce`, data));
    return res.data.ce;
  },

  async updateCe(id, data) {
    const res = await request(apiService.put(`${PREFIX}/ce/${id}`, data));
    return res.data.ce;
  },

  async deleteCe(id) {
    await request(apiService.delete(`${PREFIX}/ce/${id}`));
  },

  async listCeSalaries(ceId) {
    const res = await request(apiService.get(`${PREFIX}/ce/${ceId}/salaries`));
    return res.data.salaries ?? [];
  },

  async createCeSalarie(ceId, data) {
    const res = await request(apiService.post(`${PREFIX}/ce/${ceId}/salaries`, data));
    return res.data.salarie;
  },

  async updateCeSalarie(ceId, salarieId, data) {
    const res = await request(apiService.put(`${PREFIX}/ce/${ceId}/salaries/${salarieId}`, data));
    return res.data.salarie;
  },

  async listAvis({ search = '', avis_donne = '', sans_avis = false, page = 1, limit = 50 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/avis`, {
      search, avis_donne, sans_avis, page, limit,
    }));
    return { avis: res.data.avis ?? [], pagination: res.pagination ?? {} };
  },

  async getAvisItem(id) {
    const res = await request(apiService.get(`${PREFIX}/avis/${id}`));
    return res.data.avis ?? null;
  },

  async createAvis(data) {
    const res = await request(apiService.post(`${PREFIX}/avis`, data));
    return res.data.avis;
  },

  async updateAvis(id, data) {
    const res = await request(apiService.put(`${PREFIX}/avis/${id}`, data));
    return res.data.avis;
  },

  async deleteAvis(id) {
    await request(apiService.delete(`${PREFIX}/avis/${id}`));
  },

  async importExcel({ file, clear = false } = {}) {
    const res = await request(apiService.post(`${PREFIX}/import`, { file, clear }));
    return res.data.import ?? {};
  },

  async realignExcel({ file } = {}) {
    const res = await request(apiService.post(`${PREFIX}/import/realign`, { file }));
    return res.data.realign ?? {};
  },

  async listCommerciaux() {
    const res = await request(apiService.get(`${PREFIX}/commerciaux`));
    return res.data.commerciaux ?? [];
  },

  async listUsers() {
    const res = await request(apiService.get(`${PREFIX}/users`));
    return res.data.users ?? [];
  },

  async getPipelineTemplate() {
    const res = await request(apiService.get(`${PREFIX}/pipeline-template`));
    return res.data.pipeline_template ?? null;
  },

  async updatePipelineTemplate(id, data) {
    const res = await request(apiService.put(`${PREFIX}/pipeline-template/${id}`, data));
    return res.data.pipeline_template ?? null;
  },

  async searchDolibarrThirdparties(search, limit = 20) {
    const res = await request(apiService.get(`${PREFIX}/dolibarr-thirdparties`, { search, limit }));
    return res.data.thirdparties ?? [];
  },

  async getDolibarrPrefill(thirdpartyId) {
    const res = await request(apiService.get(`${PREFIX}/dolibarr-thirdparties/${thirdpartyId}`));
    return res.data ?? null;
  },

  async getWorkflowSummary(adherentId) {
    const res = await request(apiService.get(`${PREFIX}/adherents/${adherentId}/workflow-summary`));
    return res.data.workflow_summary ?? null;
  },

  async listDolibarrDetections({ action = '', limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/dolibarr-detections`, { action, limit }));
    return res.data.detections ?? [];
  },
};

export default klubService;
